import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import AdminNav from '../../components/admin/AdminNav';
import { Ico, Icons } from '../../components/common';
import { adminAPI } from '../../services/api';

export default function AdminDashboard() {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [dbEnv, setDbEnv] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchTournaments();
    adminAPI.health().then(d => setDbEnv(d.env)).catch(() => {});
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('tennisbuzz_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setUsers(data.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchTournaments = async () => {
    try {
      const token = localStorage.getItem('tennisbuzz_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/tournaments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setTournaments(data.data);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('tennisbuzz_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/admin/users/${userId}/toggle-status`, {
        method: 'PATCH',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ active: !currentStatus })
      });
      const data = await res.json();
      if (data.success) {
        setUsers(prev => prev.map(u => u._id === userId ? { ...u, active: !currentStatus } : u));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const players = users.filter(u => u.role === 'player');
  const organizers = users.filter(u => u.role === 'organizer');

  return (
    <div style={s.container}>
      <AdminNav />
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Admin Dashboard</h1>
          <div style={s.welcome}>Welcome, {currentUser?.name}</div>
        </div>
      </div>

      <div style={s.statsGrid}>
        <div style={s.statCard} onClick={() => navigate('/admin/players')}>
          <div style={s.statIcon}><Ico d={Icons.users} size={28} color="#BDFF00" /></div>
          <div style={s.statValue}>{players.length}</div>
          <div style={s.statLabel}>Players</div>
        </div>
        <div style={s.statCard} onClick={() => navigate('/admin')}>
          <div style={s.statIcon}><Ico d={Icons.user} size={28} color="#BDFF00" /></div>
          <div style={s.statValue}>{organizers.length}</div>
          <div style={s.statLabel}>Organizers</div>
        </div>
        <div style={s.statCard} onClick={() => navigate('/admin/tournaments')}>
          <div style={s.statIcon}><Ico d={Icons.trophy} size={28} color="#BDFF00" /></div>
          <div style={s.statValue}>{tournaments.length}</div>
          <div style={s.statLabel}>Tournaments</div>
        </div>
      </div>

      {dbEnv && <div style={s.dbSwitch}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>DB:</span>
        <span style={{ ...s.dbBadge, background: dbEnv === 'prod' ? '#E8F5E9' : '#FFF3E0', color: dbEnv === 'prod' ? '#2E7D32' : '#E65100' }}>{dbEnv.toUpperCase()}</span>
        <button onClick={async () => {
          try {
            const token = localStorage.getItem('tennisbuzz_token');
            const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/admin/backup`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Backup failed'); }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `tennisbuzz-backup-${new Date().toISOString().split('T')[0]}.zip`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { window.URL.revokeObjectURL(url); document.body.removeChild(a); }, 100);
          } catch (e) { alert('Backup failed: ' + e.message); }
        }} style={{ ...s.dbBtn, background: '#00205B', color: '#BDFF00', border: 'none', display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <Ico d={Icons.share} size={12} color="#BDFF00" />
          Download Backup
        </button>
      </div>}

      <div style={s.section}>
        <h2 style={s.sectionTitle}>Organizers</h2>
        {isMobile ? (
          <div style={s.mobileCards}>
            {organizers.map(user => (
              <div key={user._id} style={s.mobileCard}>
                <div style={s.mobileCardHeader}>
                  <div>
                    <div style={s.mobileCardName}>{user.name}</div>
                    <div style={s.mobileCardUsername}>@{user.username}</div>
                  </div>
                  <span style={{ ...s.badge, ...(user.active ? s.badgeActive : s.badgeInactive) }}>
                    {user.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {user.orgName && <div style={s.mobileCardRow}><span style={s.mobileCardLabel}>Organization</span> <span style={{ flex: 1 }}>{user.orgName}</span></div>}
                {user.orgPhone && <div style={s.mobileCardRow}><span style={s.mobileCardLabel}>Phone</span> <span style={{ flex: 1 }}>{user.orgPhone}</span></div>}
                {user.orgEmail && <div style={s.mobileCardRow}><span style={s.mobileCardLabel}>Email</span> <span style={{ flex: 1, wordBreak: 'break-all' }}>{user.orgEmail}</span></div>}
                {user.orgCity && <div style={s.mobileCardRow}><span style={s.mobileCardLabel}>City</span> <span style={{ flex: 1 }}>{user.orgCity}</span></div>}
                <button 
                  onClick={() => toggleUserStatus(user._id, user.active)}
                  style={{ ...s.toggleBtn, ...(user.active ? s.toggleBtnDeactivate : s.toggleBtnActivate), width: '100%', marginTop: 12 }}
                >
                  {user.active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={s.table}>
            <div style={s.tableHeader}>
              <div style={{ ...s.tableCell, flex: 2 }}>Name</div>
              <div style={{ ...s.tableCell, flex: 2 }}>Username</div>
              <div style={{ ...s.tableCell, flex: 2 }}>Organization</div>
              <div style={{ ...s.tableCell, flex: 1.5 }}>Contact</div>
              <div style={{ ...s.tableCell, flex: 1 }}>Status</div>
              <div style={{ ...s.tableCell, flex: 1 }}>Action</div>
            </div>
            {organizers.map(user => (
              <div key={user._id} style={s.tableRow}>
                <div style={{ ...s.tableCell, flex: 2, fontWeight: 600 }}>{user.name}</div>
                <div style={{ ...s.tableCell, flex: 2, color: '#5F6B80' }}>{user.username}</div>
                <div style={{ ...s.tableCell, flex: 2, color: '#5F6B80' }}>{user.orgName || '-'}</div>
                <div style={{ ...s.tableCell, flex: 1.5, fontSize: 12, color: '#5F6B80' }}>
                  {user.orgPhone && <div>{user.orgPhone}</div>}
                  {user.orgEmail && <div>{user.orgEmail}</div>}
                </div>
                <div style={{ ...s.tableCell, flex: 1 }}>
                  <span style={{ ...s.badge, ...(user.active ? s.badgeActive : s.badgeInactive) }}>
                    {user.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div style={{ ...s.tableCell, flex: 1 }}>
                  <button 
                    onClick={() => toggleUserStatus(user._id, user.active)}
                    style={{ ...s.toggleBtn, ...(user.active ? s.toggleBtnDeactivate : s.toggleBtnActivate) }}
                  >
                    {user.active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  container: { minHeight: 'calc(100vh - 52px)', background: 'var(--bg)', padding: '16px' },
  header: { marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 800, color: '#00205B', fontFamily: "'Outfit', sans-serif", marginBottom: 4 },
  welcome: { fontSize: 13, color: '#8892A6' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 24 },
  statCard: { background: 'linear-gradient(135deg, #00205B 0%, #0A2E6E 100%)', padding: '14px 10px', borderRadius: 12, border: '1px solid rgba(189,255,0,0.1)', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,32,91,0.15)', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' },
  statIcon: { marginBottom: 6, display: 'flex', justifyContent: 'center' },
  statValue: { fontSize: 24, fontWeight: 800, color: '#BDFF00', fontFamily: "'Outfit', sans-serif", lineHeight: 1 },
  statLabel: { fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 6, fontWeight: 600, letterSpacing: 0.3 },
  section: { background: '#fff', borderRadius: 12, border: '1px solid #EEF1F6', padding: '16px', overflowX: 'auto' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#00205B', marginBottom: 16, fontFamily: "'Outfit', sans-serif" },
  table: { width: '100%', minWidth: 800 },
  tableHeader: { display: 'flex', padding: '12px 16px', background: '#F8F9FC', borderRadius: 8, marginBottom: 8, fontWeight: 700, fontSize: 12, color: '#5F6B80' },
  tableRow: { display: 'flex', padding: '14px 16px', borderBottom: '1px solid #EEF1F6', alignItems: 'center' },
  tableCell: { display: 'flex', flexDirection: 'column', justifyContent: 'center', fontSize: 13 },
  badge: { display: 'inline-block', padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600 },
  badgeActive: { background: '#E8F5E9', color: '#2E7D32' },
  badgeInactive: { background: '#FFF3E0', color: '#E65100' },
  toggleBtn: { padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' },
  toggleBtnActivate: { background: '#E8F5E9', color: '#2E7D32' },
  toggleBtnDeactivate: { background: '#FFEBEE', color: '#C62828' },
  mobileCards: { display: 'flex', flexDirection: 'column', gap: 12 },
  mobileCard: { background: 'var(--card-bg)', padding: 16, borderRadius: 12, border: '1px solid var(--card-border)', boxShadow: '0 2px 8px rgba(0,32,91,0.04)' },
  mobileCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--card-border)' },
  mobileCardName: { fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3, fontFamily: "'Outfit', sans-serif" },
  mobileCardUsername: { fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 },
  mobileCardRow: { fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 6 },
  mobileCardLabel: { fontWeight: 700, color: 'var(--text-primary)', minWidth: 90, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.3 },
  dbSwitch: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10 },
  dbBadge: { fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6 },
  dbBtn: { marginLeft: 'auto', fontSize: 11, fontWeight: 600, padding: '5px 14px', borderRadius: 6, border: '1px solid var(--card-border)', background: 'var(--gray-50)', color: 'var(--text-primary)', cursor: 'pointer' },
};
